import React from 'react';
import { FiEye, FiEdit2, FiRepeat, FiMoreHorizontal } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SkillBadge from '../../../common/skillBadge';
import { toTitleCase } from '../../../common/common';
import { useDispatch, useSelector } from "react-redux";
import { setSearchFilters, clearSearchFilters } from "../../../redux/searchFiltersSlice";

const formatPostedOnDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const now = new Date();
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffTime = nowOnly - dateOnly;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays > 0 && diffDays <= 5) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Updated helper function to format multiple recruiters
const formatRecruiters = (recruiters) => {
  if (!recruiters || recruiters.length === 0) {
    return (
      <span className="italic text-xs" title="Not Assigned">
        Not Assigned
      </span>
    );
  }

  const names = recruiters
    .map((r) =>
      toTitleCase(`${r.first_name || ""} ${r.last_name || ""}`.trim())
    )
    .filter((name) => name);

  if (names.length === 0) {
    return (
      <span className="italic text-xs" title="Not Assigned">
        Not Assigned
      </span>
    );
  }

  const displayText =
    names.length > 1 ? `${names[0]} + ${names.length - 1} more` : names[0];

  const fullNamesTitle = names.length > 1 ? names.join(", ") : names[0];

  return (
    <span className="text max-w-[120px] truncate" title={fullNamesTitle}>
      {displayText}
    </span>
  );
};

const JobItem = ({
  job,
  searchTerm,
  selectedStatus = [],
  startDate,
  endDate,
  activeTab,
  dynamicStatuses,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewJobClick = () => {
    const statusValuesFromNames =
      selectedStatus.length > 0
        ? dynamicStatuses
            .filter((s) => selectedStatus.includes(s.name))
            .map((s) => s.value)
        : [];

    // Dispatch search filters
    dispatch(
      setSearchFilters({
        searchTerm: searchTerm,
        statusNames: selectedStatus,
        statusValues: statusValuesFromNames,
        startDate,
        endDate,
        activeTabJobsPage: activeTab,
      })
    );

    navigate(`/jobs/jobDetails/${job.mph_jobid}`);
  };  

  const iconButtonStyles =
    "h-7 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:bg-gray-100 hover:text-emerald-600 transition-colors";

  return (
    <div
      className="text-left bg-white p-6 rounded-2xl flex flex-col gap-4 shadow-lg border border-gray-100  hover:bg-gray-50 hover:shadow-l transition anim-slideUp"
    >
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <div className="flex items-center gap-3">
            {job.title && (
              <h4 className="font-bold text-base text-gray-800 cursor-pointer" onClick={handleViewJobClick}>
                <SkillBadge size="lg">{job.title} - {job.mph_jobid}</SkillBadge>
              </h4>
            )}
            {job.jobopening_status_name && (
              <SkillBadge size="xs">{job.jobopening_status_name}</SkillBadge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 pl-[5px]">{job.mph_clientid_value}</p>
        </div>

        <div className="flex items-center gap-2">
          <button className={`${iconButtonStyles} flex gap-1.5 text-sm font-semibold px-2`}>
            <FiEye className="mph-primary" size={13} /> {job.view_count}
          </button>

          <button className={`${iconButtonStyles} hidden w-7`}>
            <FiEdit2 className="mph-primary" size={13} />
          </button>

          <button className={`${iconButtonStyles} hidden w-7`}>
            <FiRepeat className="mph-primary" size={13} />
          </button>

          <button className={`${iconButtonStyles} hidden w-7`}>
            <FiMoreHorizontal className="mph-primary" size={13} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4 text-sm text-gray-600 border-t border-gray-100 pt-4">
        <div>
          <p className="text-xs text-gray-999">Vacancies</p>
          <p className="font-semibold text-gray-333">{job.vacancy}</p>
        </div>

        <div>
          <p className="text-xs text-gray-999">Deadline</p>
          <p className="font-semibold text-gray-333">
            {formatPostedOnDate(job.application_deadline_datetime)}
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-999">Location</p>
          <p className="font-semibold text-gray-333">
            {(job.location || job.city || "-")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-999">Posted on</p>
          <p className="font-semibold text-gray-333">
            {formatPostedOnDate(job.job_opening_date)}
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-999">Recruiter Name</p>
          <p className="font-semibold text-gray-333">
            {job?.recruiters?.length > 0 ? (
              formatRecruiters(job.recruiters)
            ) : job?.recruiters_first_name || job?.recruiters_last_name ? (
              toTitleCase(
                `${job.recruiters_first_name || ""} ${
                  job.recruiters_last_name || ""
                }`.trim()
              )
            ) : (
              <span className="italic text-xs">Not Assigned</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default JobItem;
